import { type CollabManager } from '@databases/collab-state-manager/collab-manager';
import { DATABASE_BE_STREAMING_PARTICIPANT } from '@databases/database-ai-first-creation-state';
import { presenceState } from '@databases/presence';
import { defaultStoreUtils } from '@databases/redux';
import { applyDatabaseDiff } from '@databases/utils/redux/databases/actions';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags';

import {
  getBackendAgentDeletedFieldIds,
  isFieldOrderingChangeDeletionOnly
} from '../backend-agent-field-deletions.ts';
import { getBackendAgentTargetBoundsById } from '../backend-agent-progressive-overlay.tsx';

export const BACKEND_AGENT_FIELD_DELETE_OVERLAY_DELAY_MS = 700;

const MAX_REVEAL_TICKS = 4;
const DEFERRED_FIELD_PROJECTION_META_KEY = Symbol.for(
  'databases:deferred-field-projection'
);

type DatabaseTransaction = Parameters<CollabManager['database']['getDiff']>[0];

interface UseBackendAgentFieldDeleteOverlayOptions {
  collabManager: CollabManager | undefined;
  contentId: string;
  isPaused: boolean;
  layoutRoot: HTMLElement | null;
  onEligibleBackendAgentFieldDelete?: () => void;
  resetKey: string;
}

export type BackendAgentFieldDeleteOverlayResult =
  | { kind: 'idle' }
  | {
      activeFieldId: string;
      kind: 'revealing';
    };

export function useBackendAgentFieldDeleteOverlay({
  collabManager,
  contentId,
  isPaused,
  layoutRoot,
  onEligibleBackendAgentFieldDelete,
  resetKey
}: UseBackendAgentFieldDeleteOverlayOptions): BackendAgentFieldDeleteOverlayResult {
  const isDatabaseBEStreamingEnabled = getExperimentValue(
    'databaseBEStreaming',
    'enabled',
    { fireExperimentExposure: false }
  );
  const dispatch = defaultStoreUtils.useDispatch();
  const previousFieldIds = useRef<readonly string[]>([]);
  const revealBatchSize = useRef(1);
  const activeBatchSize = useRef(1);
  const queuedFieldIdsRef = useRef<readonly string[]>([]);
  const [queuedFieldIds, setQueuedFieldIds] = useState<readonly string[]>([]);
  const activeFieldId =
    queuedFieldIds[Math.min(activeBatchSize.current, queuedFieldIds.length) - 1];

  const replaceQueuedFieldIds = useCallback((nextQueuedFieldIds: readonly string[]) => {
    queuedFieldIdsRef.current = nextQueuedFieldIds;
    if (nextQueuedFieldIds.length === 0) {
      revealBatchSize.current = 1;
      activeBatchSize.current = 1;
    }
    setQueuedFieldIds(nextQueuedFieldIds);
  }, []);

  const projectFieldDeletions = useCallback(
    (fieldIds: readonly string[]) => {
      if (!collabManager || fieldIds.length === 0) {
        return;
      }

      dispatch(
        applyDatabaseDiff({
          contentId,
          diff: {
            fieldsOrderingChanged: false,
            entriesOrderingChanged: false,
            changedFieldIds: [...fieldIds],
            changedEntryIds: [],
            threadsChanged: false,
            viewsChanged: false,
            defaultEntryChanged: false,
            othersChanged: false
          },
          collabDatabase: collabManager.database,
          isReadOnly: collabManager.isReadOnly
        })
      );
    },
    [collabManager, contentId, dispatch]
  );

  useLayoutEffect(() => {
    queuedFieldIdsRef.current = [];
    revealBatchSize.current = 1;
    activeBatchSize.current = 1;
    setQueuedFieldIds([]);

    if (!collabManager) {
      previousFieldIds.current = [];
      return;
    }

    previousFieldIds.current = collabManager.database.fields;
    const onAfterTransaction = (transaction: DatabaseTransaction) => {
      const diff = collabManager.database.getDiff(transaction);
      const currentFieldIds = collabManager.database.fields;
      const previousTransactionFieldIds = previousFieldIds.current;
      previousFieldIds.current = currentFieldIds;

      if (!diff.fieldsOrderingChanged) {
        return;
      }

      const deletedFieldIds = getBackendAgentDeletedFieldIds({
        currentFieldIds,
        previousFieldIds: previousTransactionFieldIds,
        transaction
      });
      if (
        deletedFieldIds.length === 0 ||
        !isFieldOrderingChangeDeletionOnly({
          currentFieldIds,
          deletedFieldIds,
          previousFieldIds: previousTransactionFieldIds
        })
      ) {
        if (queuedFieldIdsRef.current.length > 0) {
          projectFieldDeletions(queuedFieldIdsRef.current);
          replaceQueuedFieldIds([]);
        }
        return;
      }

      if (!layoutRoot) {
        return;
      }

      const deletionBoundsById = getBackendAgentTargetBoundsById({
        attribute: 'data-field-id',
        ids: deletedFieldIds,
        layoutRoot
      });
      const visibleFieldDeletes = deletedFieldIds.filter((fieldId) =>
        deletionBoundsById.has(fieldId)
      );
      if (visibleFieldDeletes.length === 0) {
        return;
      }

      onEligibleBackendAgentFieldDelete?.();
      if (!isDatabaseBEStreamingEnabled) {
        return;
      }

      transaction.meta.set(DEFERRED_FIELD_PROJECTION_META_KEY, {
        fieldIds: visibleFieldDeletes,
        deferFieldsOrdering: true
      });
      const nextQueuedFieldIds = [...queuedFieldIdsRef.current, ...visibleFieldDeletes];
      revealBatchSize.current = Math.max(
        revealBatchSize.current,
        Math.ceil((nextQueuedFieldIds.length - 1) / MAX_REVEAL_TICKS)
      );
      if (queuedFieldIdsRef.current.length === 0) {
        activeBatchSize.current = 1;
      }
      replaceQueuedFieldIds(nextQueuedFieldIds);
    };

    collabManager.database.doc.on('afterTransaction', onAfterTransaction);
    return () => {
      collabManager.database.doc.off('afterTransaction', onAfterTransaction);
      projectFieldDeletions(queuedFieldIdsRef.current);
      queuedFieldIdsRef.current = [];
    };
  }, [
    collabManager,
    isDatabaseBEStreamingEnabled,
    layoutRoot,
    onEligibleBackendAgentFieldDelete,
    projectFieldDeletions,
    replaceQueuedFieldIds,
    resetKey
  ]);

  useEffect(() => {
    if (!activeFieldId || isPaused) {
      return;
    }

    presenceState.addParticipant(
      { fieldId: activeFieldId },
      DATABASE_BE_STREAMING_PARTICIPANT
    );
    const timeout = setTimeout(() => {
      const batchSize = activeBatchSize.current;
      const currentQueuedFieldIds = queuedFieldIdsRef.current;
      projectFieldDeletions(currentQueuedFieldIds.slice(0, batchSize));
      const nextQueuedFieldIds = currentQueuedFieldIds.slice(batchSize);
      activeBatchSize.current = Math.min(
        revealBatchSize.current,
        nextQueuedFieldIds.length
      );
      replaceQueuedFieldIds(nextQueuedFieldIds);
    }, BACKEND_AGENT_FIELD_DELETE_OVERLAY_DELAY_MS);
    return () => {
      clearTimeout(timeout);
      presenceState.removeParticipant(DATABASE_BE_STREAMING_PARTICIPANT);
    };
  }, [activeFieldId, isPaused, projectFieldDeletions, replaceQueuedFieldIds]);

  return isDatabaseBEStreamingEnabled && activeFieldId && !isPaused
    ? { activeFieldId, kind: 'revealing' }
    : { kind: 'idle' };
}
